import type { ReactNode } from "react";

import { AuthShell } from "@/components/AuthShell";
import { cn } from "@/lib/utils";

interface RequestAccessShellProps {
  step: 1 | 2 | 3;
  /**
   * Overrides the landmark-labelled `<h1>`. The form steps keep the default
   * "Request access to ResetData PRM"; the success step passes "Request
   * received" so the `<main aria-labelledby>` accessible name reflects the
   * actual state (CHP-805) instead of always announcing the form heading.
   */
  heading?: string;
  children: ReactNode;
}

const STEP_LABELS: Record<1 | 2 | 3, string> = {
  1: "Referral code",
  2: "Your details",
  3: "Confirmation",
};

export function RequestAccessShell({
  step,
  heading = "Request access to ResetData PRM",
  children,
}: RequestAccessShellProps) {
  return (
    <AuthShell
      headingId="request-access-heading"
      showLogo
      overline="Onboarding"
      heading={heading}
      maxWidthClassName="max-w-lg"
      wrapperClassName="min-h-screen flex items-center justify-center px-4 py-10"
    >
      <ol aria-label="Progress" className="grid grid-cols-3 gap-2 text-xs font-medium">
        {([1, 2, 3] as const).map((n) => (
          <li
            key={n}
            aria-current={n === step ? "step" : undefined}
            className={cn(
              "rounded-md border px-3 py-2 text-center transition-colors",
              n === step
                ? "border-primary bg-primary/10 text-foreground"
                : n < step
                  ? "border-border text-muted-foreground"
                  : "border-border/50 text-muted-foreground/70",
            )}
          >
            <span className="mr-1 font-semibold">{n}.</span>
            {STEP_LABELS[n]}
          </li>
        ))}
      </ol>

      <div className="space-y-4">{children}</div>
    </AuthShell>
  );
}
